import * as React from "react";
import { useMemo } from "react";
import {
  Edit,
  TextInput,
  DateTimeInput,
  useRecordContext,
  Title,
  FormDataConsumer,
  ArrayInput,
  SimpleFormIterator,
  useSimpleFormIteratorItem,
  ReferenceInput,
  AutocompleteInput,
  SelectInput,
  RecordContextProvider,
  useGetList,
  Loading,
  DateInput,
} from "react-admin";
import { Box, Typography, Divider } from "@mui/material";
import { SimpleForm } from "../common/SimpleForm";
import {
  validateRequired,
  validateProductEventName,
  validateProductEventDates,
  validateProductEventEndDate,
} from "../utils/Validations";
import Spacer from "../common/Spacer";
import { RESTAURANT_TYPES } from "../enums/RestaurantTypes";
import { ENTITY_AVAILABILITY_STATUS } from "../enums/EntityAvailabilityStatus";
import { useWatch } from "react-hook-form";
import AbsoluteSource from "../common/AbsoluteSource";

const ProductEventTitle = () => {
  const record = useRecordContext();
  return <Title title={`Evento "${record?.name || ""}"`} />;
};

const transform = (data) => {
  const { restaurantProduct, products, ...rest } = data;
  return {
    ...rest,
    to: rest.to ?? rest.from,
    products: products?.filter(Boolean) || [],
  };
};

const transformRecordWithProducts = (record, linkedProducts) => {
  if (!record) return record;

  const products = linkedProducts || [];
  const productIds = products.map((p) => p.id);
  const restaurantProduct = products.map((p) => p.restaurant);

  return {
    ...record,
    products: productIds,
    restaurantProduct,
  };
};

const ProductEventProductRow = () => {
  const { index } = useSimpleFormIteratorItem();
  const restaurantProduct = useWatch({ name: "restaurantProduct" });
  return (
    <Box sx={{ flex: "1" }}>
      <Box sx={{ display: "flex" }}>
        <AbsoluteSource>
          <ReferenceInput
            source={`restaurantProduct[${index}]`}
            reference="restaurants"
            perPage={100}
            filter={{
              active: true,
              type: RESTAURANT_TYPES.REGULAR,
            }}
            fullWidth
          >
            <AutocompleteInput
              filterToQuery={(searchText) => ({
                name: searchText,
              })}
              label="Restaurante"
              optionText="name"
              fullWidth
            />
          </ReferenceInput>
        </AbsoluteSource>

        <Spacer />

        <ReferenceInput
          // Empty source = the array's scalar element (products.<i>)
          source=""
          reference="products"
          perPage={10000}
          sort={{ field: "name", order: "ASC" }}
          filter={{
            restaurant: restaurantProduct?.[index],
            status: [ENTITY_AVAILABILITY_STATUS.ACTIVE],
          }}
          fullWidth
        >
          <SelectInput
            optionText="name"
            label="Producto"
            readOnly={!restaurantProduct?.[index]}
            fullWidth
          />
        </ReferenceInput>
      </Box>
    </Box>
  );
};

const ProductEventForm = () => {
  const record = useRecordContext();

  const { data: linkedProducts, isPending } = useGetList(
    "products",
    {
      filter: { event: record?.id },
      pagination: { page: 1, perPage: 1000 },
    },
    { enabled: !!record?.id }
  );

  const transformedRecord = useMemo(
    () => transformRecordWithProducts(record, linkedProducts),
    [record, linkedProducts]
  );

  // The products query is disabled until the record arrives, and a disabled query
  // stays pending forever, so gate on the record as well — otherwise isPending
  // alone would keep this on <Loading /> for good.
  if (!record?.id || isPending) {
    return <Loading />;
  }

  return (
    <RecordContextProvider value={transformedRecord}>
      <SimpleForm record={transformedRecord}>
        <FormDataConsumer>
          {({ formData }) => (
            <>
              <TextInput
                source="name"
                label="Nombre"
                validate={validateProductEventName}
                fullWidth
              />

              <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                <DateTimeInput
                  source="fromPresale"
                  label="Preventa desde"
                  parse={(v) => (v ? new Date(v).toISOString() : null)}
                  validate={validateRequired}
                  fullWidth
                />
                <DateInput
                  source="from"
                  label="Evento desde"
                  parse={(v) =>
                    v ? new Date(`${v}T03:00:00Z`).toISOString() : null
                  }
                  validate={[validateRequired, validateProductEventDates]}
                  fullWidth
                />
                <DateInput
                  source="to"
                  label="Evento hasta"
                  parse={(v) =>
                    v ? new Date(`${v}T03:00:00Z`).toISOString() : null
                  }
                  validate={[validateRequired, validateProductEventEndDate]}
                  fullWidth
                />
              </Box>

              <Box sx={{ width: "100%", my: 3 }}>
                <Divider />
              </Box>

              <Typography variant="h6" gutterBottom>
                Productos del Evento
              </Typography>

              <ArrayInput source="products" label="">
                <SimpleFormIterator disableReordering>
                  <ProductEventProductRow />
                </SimpleFormIterator>
              </ArrayInput>
            </>
          )}
        </FormDataConsumer>
      </SimpleForm>
    </RecordContextProvider>
  );
};

const ProductEventEdit = () => (
  <Edit
    title={<ProductEventTitle />}
    mutationMode="pessimistic"
    transform={transform}
  >
    <ProductEventForm />
  </Edit>
);

export default ProductEventEdit;
